import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SlidersHorizontal, Check, ChevronRight, RotateCcw } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'

export interface ProductFilterOption {
  value: string
  label: string
}

export interface ProductFilterFacet {
  key: string
  label: string
  /**
   * Presentation/behaviour hint. ``variant`` matters most: the admin library
   * reveals variant rows only when a ``variant`` facet is selected. ``field``
   * is a structural column (template/category/status/type).
   */
  kind: 'category' | 'brand' | 'variant' | 'product' | 'field'
  /**
   * Data type of the facet, driving how it renders. ``select`` (default) is a
   * multi-select checkbox list; ``date`` is a single-select preset list
   * (picking one replaces the previous choice). Backend-provided.
   */
  type?: 'select' | 'date'
  options: ProductFilterOption[]
}

/** Map of facet key -> selected option values. */
export type ProductFilterValue = Record<string, string[]>

interface ProductFilterMenuProps {
  facets: ProductFilterFacet[]
  value: ProductFilterValue
  onChange: (value: ProductFilterValue) => void
  /**
   * Extra classes for the trigger button so each surface can align it to its
   * own toolbar. The consumer keeps the default (matches its h-9 search input
   * + view toggle); the admin library passes the compact toolbar style so the
   * trigger sits flush with FILTER / COLUMNS / EXPORT.
   */
  triggerClassName?: string
}

/**
 * Cascading filter menu shared by the consumer product browser and the admin
 * product library. Each facet is a row that opens its own submenu (to the
 * side) of checkable options; a selected row shows a tick and its chosen
 * value(s); a Reset action clears everything. Renders nothing when there are
 * no facets AND no active selection; if filters are applied but the facet list
 * is empty (a transient error or an empty filtered result set) the trigger
 * stays mounted so Reset remains reachable. The caller owns which facets it
 * passes (e.g. the consumer feeds
 * consumer-visible facets, the admin feeds all variant attributes) and how the
 * selected values map to query params.
 */
export function ProductFilterMenu({
  facets,
  value,
  onChange,
  triggerClassName,
}: ProductFilterMenuProps) {
  const { t } = useTranslation()
  const [openKey, setOpenKey] = useState<string | null>(null)

  const activeCount = Object.values(value).reduce((n, v) => n + v.length, 0)

  // Hide only when there is genuinely nothing to show. If the facet list is
  // empty but filters are still applied (e.g. the list query errored or the
  // filtered result set produced no facets), keep the trigger mounted so the
  // Reset action stays reachable - otherwise the user is stranded with filters
  // they can no longer clear.
  if (!facets.length && activeCount === 0) return null

  function toggle(facetKey: string, optionValue: string, single = false) {
    const current = value[facetKey] ?? []
    // Single-select (e.g. a date preset): picking a new option replaces the
    // previous choice; re-picking the active one clears it.
    const next = single
      ? current.includes(optionValue)
        ? []
        : [optionValue]
      : current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue]
    const merged = { ...value, [facetKey]: next }
    if (!next.length) delete merged[facetKey]
    onChange(merged)
  }

  function summarise(facet: ProductFilterFacet): string {
    const selected = value[facet.key] ?? []
    if (selected.length === 0) return ''
    const labels = selected.map(
      (v) => facet.options.find((o) => o.value === v)?.label ?? v,
    )
    if (labels.length <= 2) return labels.join(', ')
    return t('register.filterSelectedCount', {
      count: labels.length,
      defaultValue: '{{count}} selected',
    })
  }

  return (
    <Popover onOpenChange={(open) => !open && setOpenKey(null)}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            className={cn(
              'flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-[8px] border-[0.5px] border-solid px-3 text-[13px]',
              activeCount > 0
                ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                : 'border-[var(--rule)] bg-[var(--bg)] text-[var(--ink)]',
              triggerClassName,
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {t('register.filters', { defaultValue: 'Filters' })}
            {activeCount > 0 && (
              <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--green)] px-1 text-[11px] text-white">
                {activeCount}
              </span>
            )}
          </Button>
        }
      />
      <PopoverContent side="bottom" align="end" className="w-[220px] p-1">
        {activeCount > 0 && (
          <div className="mb-1 flex justify-end border-b-[0.5px] border-b-[var(--rule)] pb-1.5">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onChange({})
                setOpenKey(null)
              }}
              className="flex h-auto cursor-pointer items-center gap-1.5 rounded-[6px] px-2 py-1 text-[12px] text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t('register.reset', { defaultValue: 'Reset' })}
            </Button>
          </div>
        )}

        <div className="flex flex-col">
          {facets.map((facet) => {
            const selected = value[facet.key] ?? []
            const isActive = selected.length > 0
            const summary = summarise(facet)
            return (
              <div
                key={facet.key}
                className="relative"
                onMouseEnter={() => setOpenKey(facet.key)}
                onMouseLeave={() => setOpenKey(null)}
              >
                <div
                  role="menuitem"
                  tabIndex={0}
                  aria-haspopup="menu"
                  aria-expanded={openKey === facet.key}
                  onClick={() =>
                    setOpenKey(openKey === facet.key ? null : facet.key)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
                      e.preventDefault()
                      setOpenKey(facet.key)
                    }
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-[6px] px-2 py-1.5 text-[13px] text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
                >
                  <span className="flex w-4 shrink-0 justify-center text-[var(--green)]">
                    {isActive && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <span className="flex-1 truncate">
                    {facet.label}
                    {summary && (
                      <span className="text-[var(--ink-3)]"> - {summary}</span>
                    )}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-[var(--ink-3)]" />
                </div>

                {openKey === facet.key && (
                  <div className="absolute right-full top-0 z-10 pr-1.5">
                    <div className="max-h-[280px] w-[190px] overflow-y-auto rounded-[10px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg-card)] p-1 shadow-[var(--shadow-float)]">
                      {facet.options.map((opt) => {
                        const checked = selected.includes(opt.value)
                        const single = facet.type === 'date'
                        return (
                          <div
                            key={opt.value}
                            role={single ? 'menuitemradio' : 'menuitemcheckbox'}
                            aria-checked={checked}
                            tabIndex={0}
                            onClick={() => toggle(facet.key, opt.value, single)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                toggle(facet.key, opt.value, single)
                              }
                            }}
                            className="flex cursor-pointer items-center gap-2 rounded-[6px] px-2 py-1.5 text-[13px] text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
                          >
                            <span className="flex w-4 shrink-0 justify-center text-[var(--green)]">
                              {checked && <Check className="h-3.5 w-3.5" />}
                            </span>
                            <span className="flex-1 truncate">{opt.label}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
